import { Connection, DeleteConnectionOutcome, SaveConnectionOptions } from '../../../types/electron';

/**
 * Gets all saved connections from the electron layer.
 * Note: Passwords are not decrypted - use getDecryptedPassword() when connecting.
 * @returns Promise with the list of connections
 */
export async function getConnections(): Promise<Connection[]> {
  if (window.electronAPI) {
    return window.electronAPI.getConnections();
  }
  return [];
}

/**
 * Gets the decrypted password for a connection.
 * This triggers keychain access (on macOS) only when needed.
 * @param connectionId The connection ID to get password for
 * @returns Promise with the decrypted password, or empty string if not found
 */
export async function getDecryptedPassword(connectionId: string): Promise<string> {
  if (window.electronAPI) {
    return window.electronAPI.getDecryptedPassword(connectionId);
  }
  return '';
}

/**
 * Saves a connection to the electron layer (upsert)
 * @param connection The connection to save
 * @param options What THIS save says about itself (`passwordEdited`) — a
 *   separate argument, never a field of `connection`, so it cannot be stored
 *   and re-sent by a later background save of the same record.
 * @returns Promise with success status
 */
export async function saveConnection(
  connection: Connection,
  options?: SaveConnectionOptions,
): Promise<boolean> {
  try {
    if (!window.electronAPI) {
      console.error('Electron API is not available');
      return false;
    }

    return await window.electronAPI.saveConnection(connection, options);
  } catch (error) {
    console.error('Error in saveConnection:', error);
    return false;
  }
}

/**
 * Stamps `lastConnectedAt` on one saved connection after a successful connect.
 *
 * Deliberately NOT a `saveConnection` of the whole record: that made the
 * "connected" paint wait on an encrypt-and-rewrite of the entire connections
 * file, and it wrote the renderer's copy of every other field of the record
 * over whatever had been edited while the dial was in flight. Main writes the
 * one field, or nothing at all when the record was deleted meanwhile.
 *
 * Fire-and-forget: the timestamp only orders the connections list, so a
 * failure is logged and swallowed rather than failing the connect.
 *
 * @param connectionId The connection that just connected
 * @param lastConnectedAt ISO-8601 timestamp
 */
export async function touchConnectionLastConnected(
  connectionId: string,
  lastConnectedAt: string,
): Promise<boolean> {
  try {
    if (!window.electronAPI) return false;
    return await window.electronAPI.touchConnectionLastConnected(connectionId, lastConnectedAt);
  } catch (error) {
    console.error('Error in touchConnectionLastConnected:', error);
    return false;
  }
}

/**
 * Deletes a connection from the electron layer.
 *
 * Structured (A10): the caller has to tell a REFUSAL ('still-connected' — the
 * record is intact and the user can disconnect and try again) from a write that
 * failed AFTER the teardown ('write-failed'), which leaves a listed connection
 * with no pool behind it. A collapsed boolean told the user "it is still
 * connected" for both. Anything unreachable or unexpected reads as
 * 'write-failed': nothing was proven removed.
 *
 * @param connectionId The connection ID to delete
 */
export async function deleteConnection(connectionId: string): Promise<DeleteConnectionOutcome> {
  try {
    if (!window.electronAPI) {
      console.error('Electron API is not available');
      return { deleted: false, reason: 'write-failed' };
    }

    return await window.electronAPI.deleteConnection(connectionId);
  } catch (error) {
    console.error('Error in deleteConnection:', error);
    return { deleted: false, reason: 'write-failed' };
  }
}